import * as DG from "ui/dialogs"

import {
  BoCancellationErrors,
  FileReaderType,
  SubmitCancellationModalProps,
} from "models/bookingOrdersCancellation"
import { Document, FileType } from "models/documents"
import { DropArea, getAcceptableFiles } from "components/Files"
import { FileRejection, useDropzone } from "react-dropzone"
import React, { useCallback, useEffect, useState } from "react"

import Alert from "@material-ui/lab/Alert"
import { ApiError } from "api/http/utils"
import Button from "ui/buttons/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import { Item } from "components/Documents"
import TextField from "@material-ui/core/TextField"
import { cancelBookingOrder } from "api/bookingOrder"
import { css } from "@emotion/css"
import { format } from "date-fns"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useMutation } from "react-query"
import { v4 as uuidv4 } from "uuid"
import { validateString } from "utils/validations"
import { BO_CANCEL_MAX_FILE_SIZE_BYTES } from "routes/Base/BookingOrders/constants"

const acceptList = getAcceptableFiles(["PNG", "JPG", "DOC", "PDF"])
const allowMultipleFiles = false

const getFileTypeByName = (name: string): FileType => {
  const [, suffix] = name.split(".")
  return suffix as FileType
}

/**
 * Cancellation modal to send / view the request
 */
function SubmitCancellationModal(props: SubmitCancellationModalProps) {
  /**
   * Hooks
   */
  const { t } = useI18n()
  const [cancelledBy, setCancelledBy] = useState<string>("")
  const [cancellationDate, setCancellationDate] = useState<Date | null>(null)
  const [cancellationTime, setCancellationTime] = useState<Date | null>(null)
  const [cancellationComment, setCancellationComment] = useState<string>("")
  const [files, setFiles] = useState<Document[]>([])
  const [errors, setErrors] = useState<Partial<BoCancellationErrors>>({})
  const [fileErrors, setFileErrors] = useState<String[]>([])
  const [showGeneralError, setShowGeneralError] = useState<boolean>(false)
  const [generalErrorMessage, setGeneralErrorMessage] = useState<string>("")
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false)
  const { user } = useAuthenticated()

  useEffect(() => {
    const fullName = `${user?.family_name ?? ""} ${user?.given_name ?? ""}`

    setCancellationDate(new Date())
    setCancellationTime(new Date())
    setCancelledBy(fullName)

    return () => {
      setCancellationDate(null)
      setCancellationTime(null)
      setCancelledBy("")
    }
  }, [user])

  /**
   * Data validation then user press "Save" Button
   */
  const validateFields = useCallback(() => {
    const errors = {
      cancelledBy: validateString(cancelledBy),
      cancellationDate:
        cancellationDate !== null ? "" : "bo.cancel.submit.modal.error.date",
      cancellationTime:
        cancellationTime !== null ? "" : "bo.cancel.submit.modal.error.time",
      cancellationComment: validateString(cancellationComment),
      files:
        Array.isArray(files) && files.length > 0
          ? ""
          : "bo.cancel.submit.modal.error.file",
    }

    setErrors(errors)
    return Object.values(errors).every((val) => !Boolean(val))
  }, [
    cancelledBy,
    cancellationDate,
    cancellationTime,
    cancellationComment,
    files,
  ])

  /**
   * Request to cancel the booking order
   */
  const [cancelBo] = useMutation(cancelBookingOrder, {
    onSuccess: () => {
      setIsRequestPending(false)
      if (props.onSuccessCancellation) props.onSuccessCancellation()
    },
    onError: (error: ApiError) => {
      setIsRequestPending(false)
      if (error?.info?.status === 413) {
        setErrors({ files: "document.too.big" })
      } else {
        setGeneralErrorMessage(error?.message ?? "bo.cancel.general.error")
        setShowGeneralError(true)
      }
    },
  })

  /**
   * Serialize the cancellation date to ISO format
   */
  const buildCancellationDateTime = () => {
    let returnDateTime = ""
    if (cancellationDate) {
      returnDateTime += format(cancellationDate, "yyyy-MM-dd")

      if (cancellationTime) {
        returnDateTime += `T${format(cancellationTime, "HH:mm:ss")}Z`
      } else {
        returnDateTime += "T00:00:00Z"
      }
    }

    return returnDateTime
  }

  /**
   * Converts an array buffer to string
   */
  const convertArrayBufferToString = (fileArrBuffer: ArrayBuffer): string => {
    return btoa(String.fromCharCode(...new Uint8Array(fileArrBuffer)))
  }

  /**
   * Converts a file to base64
   */
  const getBase64 = (file: File | undefined): Promise<FileReaderType> => {
    const reader = new FileReader()
    return new Promise<FileReaderType>((resolve) => {
      reader.onload = (event) => {
        if (event) {
          let readerResult = event?.target?.result ?? ""
          if (readerResult instanceof ArrayBuffer) {
            readerResult = convertArrayBufferToString(readerResult)
          }
          resolve({
            fileName: file?.name ?? "",
            fileData: readerResult,
          })
        }
      }
      if (file) {
        reader.readAsDataURL(file)
      }
    })
  }

  /**
   * Converts an file array to base64 array
   */
  const convertFileListToBase64 = async (fileList: (File | undefined)[]) => {
    if (typeof fileList !== "undefined") {
      const promises: Promise<FileReaderType>[] = []
      for (let i = 0; i < fileList.length; i++) {
        promises.push(getBase64(fileList[i]))
      }
      return await Promise.all(promises)
    } else {
      return undefined
    }
  }

  /**
   * Cancel request to back-end
   */
  const cancel = async (args: {
    id: string
    name: string
    cancelledBy: string
    cancellationDate: Date | null
    cancellationComment: string
    files: Document[]
  }) => {
    const parsedFiles = await convertFileListToBase64(
      files.map((file) => file?.file),
    )
    cancelBo({
      cancelledBy: cancelledBy,
      cancellationDate: buildCancellationDateTime(),
      bookingOrderId: props.bookingOrderId,
      cancellationFiles: parsedFiles ?? undefined,
      cancellationComment: cancellationComment,
    })
  }

  /**
   * Validate attached file size
   */
  const validateFileSize = (arrayFiles: File[]) => {
    const maxFileSize = BO_CANCEL_MAX_FILE_SIZE_BYTES as number
    return arrayFiles
      .filter((file) => file?.size / maxFileSize > 1)
      .map((file) => file?.name)
  }

  /**
   * Callback when user drag and drop a file to droppable box
   */
  const onDrop = (acceptedFiles: File[], _: FileRejection[]) => {
    setFileErrors([])
    if (Array.isArray(acceptedFiles)) {
      const erroredFiles = validateFileSize(acceptedFiles)
      if (erroredFiles.length > 0) {
        setFileErrors(erroredFiles)
        setErrors((errors) => ({
          ...errors,
          files: "bo.cancel.submit.modal.files-error",
        }))
      } else {
        setErrors((errors) => ({ ...errors, files: "" }))

        let newFiles: Document[] = acceptedFiles.map((file) => {
          const document: Document = {
            file: file,
            fileType: getFileTypeByName(file.name),
            name: file.name,
            ownerEntityId: props.bookingOrderId,
            dateCreated: new Date().getTime(),
            id: uuidv4(),
          }
          return document
        })

        if (allowMultipleFiles && (files ?? []).length > 0) {
          newFiles = [...newFiles, ...files]
        }

        setFiles(newFiles)
      }
    }
  }

  /**
   * DropZone data
   */
  const dropzoneState = useDropzone({
    onDrop,
    accept: acceptList.accept,
    multiple: allowMultipleFiles,
    noClick: true,
  })

  /**
   * Callback when user press on "Save" Button
   */
  const onClickConfirmCancellation = () => {
    if (validateFields()) {
      setIsRequestPending(true)
      cancel({
        id: props.bookingOrderId,
        name: props.bookingOrderName,
        cancelledBy: cancelledBy,
        cancellationDate: cancellationDate ?? null,
        cancellationComment: cancellationComment,
        files: files,
      })
    }
  }

  /**
   * Callback when user changes cancellation comment field
   */
  const handleCancellationCommentsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setErrors((errors) => ({ ...errors, cancellationComment: "" }))
    setCancellationComment(e.currentTarget.value.substring(0, 255))
  }

  /**
   * Called when user press on delete button (for each file)
   */
  const handleDeleteFile = (fileId: string | undefined) => {
    const remainingFiles = files.filter((file) => file?.id !== (fileId ?? ""))
    setFiles(remainingFiles)
  }

  /**
   * Callback when user press close button
   */
  const onCloseModal = () => {}

  return (
    <DG.Dialog
      open={props.open}
      onClose={onCloseModal}
      id="bo-cancellation-modal"
    >
      <DG.DialogHeader>
        <div>
          <DG.DialogTitle id="bo-cancellation-modal-title">
            {t("bo.cancel.submit.modal.title")}
          </DG.DialogTitle>
          <p className={styles.subtitle}>
            {t("bo.cancel.confirm.modal.message")}
          </p>
        </div>
        <DG.DialogClose onClose={props.onClose} />
      </DG.DialogHeader>

      <DG.DialogContent paddingTop className={styles.container}>
        {showGeneralError && (
          <Alert
            onClose={() => {
              setShowGeneralError(false)
            }}
            severity="error"
          >
            {t(generalErrorMessage)}
          </Alert>
        )}

        <div className={styles.inputRow}>
          <TextField
            value={cancellationComment}
            variant="outlined"
            color="primary"
            multiline
            fullWidth
            rows={3}
            onChange={handleCancellationCommentsChange}
            placeholder={t("bo.cancel.confirm.modal.textarea.placeholder")}
            error={Boolean(errors.cancellationComment)}
            helperText={t(errors.cancellationComment ?? "")}
          />
        </div>
        <div className={styles.dropZoneWrapper}>
          {files &&
            files.map((file) => (
              <Item
                key={file?.id}
                document={file}
                type="campaign"
                onDelete={() => handleDeleteFile(file.id)}
              />
            ))}

          <div className={styles.fileWrapper}>
            <span className="BodyBody-2RedRegular">
              {((errors.files || "").length > 0 || fileErrors.length > 0) && (
                <Alert
                  style={{ marginBottom: 20 }}
                  severity="error"
                  onClose={() => {
                    setFileErrors([])
                    setErrors((errors) => ({ ...errors, files: "" }))
                  }}
                >
                  {errors.files === "bo.cancel.submit.modal.files-error"
                    ? fileErrors + " "
                    : ""}
                  {t(errors.files || "")}
                </Alert>
              )}
            </span>
            <DropArea
              acceptableFilesLabel={acceptList.description}
              dropzoneState={dropzoneState}
              documentsLabel={
                allowMultipleFiles
                  ? t("bo.cancel.submit.modal.files")
                  : t("bo.cancel.submit.modal.file")
              }
            />
          </div>
        </div>
      </DG.DialogContent>

      <DG.DialogFooter>
        <DG.DialogActions>
          {!isRequestPending && (
            <Button variant="outlined" color="primary" onClick={props.onClose}>
              {t("warning.modal.cancel")}
            </Button>
          )}

          {isRequestPending ? (
            <CircularProgress
              size={25}
              disableShrink
              style={{
                marginRight: 20,
              }}
            />
          ) : (
            <Button
              variant="contained"
              color="destructive"
              onClick={onClickConfirmCancellation}
            >
              {t("bo.cancel.submit.modal.button")}
            </Button>
          )}
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

export default SubmitCancellationModal

const styles = {
  container: css`
    display: grid;
    width: 100%;
    grid-gap: var(--spacing-xl);
  `,
  inputRow: css`
    display: flex;
    width: 100%;
    justify-content: center;
  `,
  dropZoneWrapper: css`
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
  `,
  fileWrapper: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  subtitle: css`
    font-weight: 400;
    font-size: 0.9rem;
    margin-top: 0.5rem;
  `,
}
